// useNotifications は NotificationsContext の薄いラッパーとして機能します
// アプリ全体で1つのインスタンスを共有し、リアルタイム購読の重複を防ぎます
import { supabase } from '../lib/supabase';
import { useNotificationsContext, Notification as NotificationFromContext } from '../contexts/NotificationsContext';

export type Notification = NotificationFromContext;

export function useNotifications() {
  return useNotificationsContext();
}

// いいね機能（日記）
export async function toggleDiaryLike(
  diaryId: string,
  diaryTitle: string,
  diaryOwnerId: string,
  currentUserId: string,
  currentUsername: string
): Promise<{ liked: boolean; count: number }> {
  const { data: existing } = await supabase
    .from('diary_likes')
    .select('id')
    .eq('diary_entry_id', diaryId)
    .eq('user_id', currentUserId)
    .maybeSingle();

  if (existing) {
    await supabase.from('diary_likes').delete().eq('id', existing.id);
    const { count } = await supabase
      .from('diary_likes')
      .select('*', { count: 'exact', head: true })
      .eq('diary_entry_id', diaryId);
    return { liked: false, count: count || 0 };
  } else {
    await supabase.from('diary_likes').insert({ diary_entry_id: diaryId, user_id: currentUserId });
    // 自分以外の投稿にいいねした場合のみ通知
    if (diaryOwnerId !== currentUserId) {
      await supabase.from('notifications').insert({
        recipient_user_id: diaryOwnerId,
        actor_user_id: currentUserId,
        type: 'diary_like',
        target_id: diaryId,
        target_type: 'diary',
        target_title: diaryTitle,
      });
    }
    const { count } = await supabase
      .from('diary_likes')
      .select('*', { count: 'exact', head: true })
      .eq('diary_entry_id', diaryId);
    return { liked: true, count: count || 0 };
  }
}

// いいね機能（資料）
export async function toggleMaterialLike(
  materialId: string,
  materialTitle: string,
  materialOwnerId: string,
  currentUserId: string
): Promise<{ liked: boolean; count: number }> {
  const { data: existing } = await supabase
    .from('material_likes')
    .select('id')
    .eq('material_id', materialId)
    .eq('user_id', currentUserId)
    .maybeSingle();

  if (existing) {
    await supabase.from('material_likes').delete().eq('id', existing.id);
    const { count } = await supabase
      .from('material_likes')
      .select('*', { count: 'exact', head: true })
      .eq('material_id', materialId);
    return { liked: false, count: count || 0 };
  } else {
    await supabase.from('material_likes').insert({ material_id: materialId, user_id: currentUserId });
    if (materialOwnerId !== currentUserId) {
      await supabase.from('notifications').insert({
        recipient_user_id: materialOwnerId,
        actor_user_id: currentUserId,
        type: 'material_like',
        target_id: materialId,
        target_type: 'material',
        target_title: materialTitle,
      });
    }
    const { count } = await supabase
      .from('material_likes')
      .select('*', { count: 'exact', head: true })
      .eq('material_id', materialId);
    return { liked: true, count: count || 0 };
  }
}

// いいね機能（達成報告）
export async function toggleAchievementLike(
  achievementId: string,
  achievementTitle: string,
  achievementOwnerId: string,
  currentUserId: string
): Promise<{ liked: boolean; count: number }> {
  const { data: existing } = await supabase
    .from('achievement_likes')
    .select('id')
    .eq('achievement_id', achievementId)
    .eq('user_id', currentUserId)
    .maybeSingle();

  if (existing) {
    await supabase.from('achievement_likes').delete().eq('id', existing.id);
    const { count } = await supabase
      .from('achievement_likes')
      .select('*', { count: 'exact', head: true })
      .eq('achievement_id', achievementId);
    return { liked: false, count: count || 0 };
  } else {
    await supabase.from('achievement_likes').insert({ achievement_id: achievementId, user_id: currentUserId });
    if (achievementOwnerId !== currentUserId) {
      await supabase.from('notifications').insert({
        recipient_user_id: achievementOwnerId,
        actor_user_id: currentUserId,
        type: 'achievement_like',
        target_id: achievementId,
        target_type: 'achievement',
        target_title: achievementTitle,
      });
    }
    const { count } = await supabase
      .from('achievement_likes')
      .select('*', { count: 'exact', head: true })
      .eq('achievement_id', achievementId);
    return { liked: true, count: count || 0 };
  }
}

// 達成報告投稿通知をグループメンバー全員に送る
export async function sendAchievementPostedNotifications(
  achievementId: string,
  achievementTitle: string,
  groupId: string,
  memberUserIds: string[],
  posterUserId: string
): Promise<void> {
  try {
    const notifications = memberUserIds
      .filter(uid => uid !== posterUserId)
      .map(uid => ({
        recipient_user_id: uid,
        actor_user_id: posterUserId,
        type: 'achievement_posted',
        target_id: achievementId,
        target_type: 'achievement',
        target_title: achievementTitle,
      }));
    if (notifications.length > 0) {
      await supabase.from('notifications').insert(notifications);
    }
  } catch (err) {
    console.error('達成報告通知エラー:', err);
  }
}

// チャットメッセージ通知をグループメンバー全員に送る
export async function sendChatMessageNotification(
  groupId: string,
  groupName: string,
  memberUserIds: string[],
  senderUserId: string
): Promise<void> {
  try {
    // 同じグループの未読チャット通知が既にある場合は重複して送らない（5分以内）
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    const { data: existing } = await supabase
      .from('notifications')
      .select('id')
      .eq('actor_user_id', senderUserId)
      .eq('type', 'chat_message')
      .eq('target_id', groupId)
      .gte('created_at', fiveMinutesAgo)
      .limit(1);

    if (existing && existing.length > 0) return;

    const notifications = memberUserIds
      .filter(uid => uid !== senderUserId)
      .map(uid => ({
        recipient_user_id: uid,
        actor_user_id: senderUserId,
        type: 'chat_message',
        target_id: groupId,
        target_type: 'group',
        target_title: groupName,
      }));
    if (notifications.length > 0) {
      await supabase.from('notifications').insert(notifications);
    }
  } catch (err) {
    console.error('チャット通知エラー:', err);
  }
}

// グループ目標達成通知をメンバー全員に送る
export async function sendGroupGoalAchievedNotifications(
  groupId: string,
  groupName: string,
  memberUserIds: string[],
  achieverUserId: string,
  weekLabel: string,
  achievedHours: number,
  goalHours: number
): Promise<void> {
  try {
    // 既に今週の達成通知を送っていないか確認（重複防止）
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + (weekStart.getDay() === 0 ? -6 : 1));
    weekStart.setHours(0, 0, 0, 0);

    const { data: existing } = await supabase
      .from('notifications')
      .select('id')
      .eq('actor_user_id', achieverUserId)
      .eq('type', 'group_goal_achieved')
      .eq('target_id', groupId)
      .gte('created_at', weekStart.toISOString())
      .limit(1);

    if (existing && existing.length > 0) return; // 今週すでに送信済み

    const notifications = memberUserIds.map(uid => ({
      recipient_user_id: uid,
      actor_user_id: achieverUserId,
      type: 'group_goal_achieved',
      target_id: groupId,
      target_type: 'group',
      target_title: `${groupName}（${weekLabel} / ${achievedHours}h達成 / 目標${goalHours}h）`,
    }));

    await supabase.from('notifications').insert(notifications);
  } catch (err) {
    console.error('グループ目標達成通知エラー:', err);
  }
}
